import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';

interface AuthDiagnostics {
  auth_uid: string | null;
  jwt_sub_claim: string | null;
  jwt_claims: any;
  current_user: string;
  session_user: string;
  timestamp: string;
}

export const AuthDiagnostics: React.FC = () => {
  const [diagnostics, setDiagnostics] = useState<AuthDiagnostics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenInfo, setTokenInfo] = useState<any>(null);

  const runDiagnostics = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get current session info
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      // Check localStorage for tokens
      const authKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('supabase.auth.') || key.includes('sb-')
      );
      
      const tokenData = {
        hasSession: !!session,
        sessionId: session?.user?.id,
        userId: user?.id,
        userEmail: user?.email,
        accessToken: session?.access_token ? 'Present' : 'Missing',
        refreshToken: session?.refresh_token ? 'Present' : 'Missing',
        expiresAt: session?.expires_at,
        authKeys: authKeys,
        sessionError: sessionError?.message,
        userError: userError?.message
      };
      
      setTokenInfo(tokenData);
      
      // Call the database diagnostic function
      const { data, error: dbError } = await supabase.rpc('debug_auth_context');
      
      if (dbError) {
        throw new Error(`Database error: ${dbError.message}`);
      }
      
      setDiagnostics(data as unknown as AuthDiagnostics);
      
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSession = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.refreshSession();
      if (error) throw error;
      
      // Re-run diagnostics after refresh
      await runDiagnostics();
    } catch (err: any) {
      setError(`Session refresh failed: ${err.message}`);
      setIsLoading(false);
    }
  };

  const getStatus = (condition: boolean) => {
    return condition ? (
      <Badge variant="default" className="bg-green-500">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        OK
      </Badge>
    ) : (
      <Badge variant="destructive">
        <AlertCircle className="w-3 h-3 mr-1" />
        FAIL
      </Badge>
    );
  };

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader>
          <CardTitle>JWT Token Transmission Diagnostics</CardTitle>
          <div className="flex gap-2">
            <Button onClick={runDiagnostics} disabled={isLoading}>
              {isLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
              Run Diagnostics
            </Button>
            <Button onClick={refreshSession} disabled={isLoading} variant="outline">
              Refresh Session
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {tokenInfo && (
            <div className="mb-6">
              <h3 className="font-semibold mb-3">Frontend Authentication State</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>Session Present: {getStatus(tokenInfo.hasSession)}</div>
                <div>User ID: {tokenInfo.userId || 'None'}</div>
                <div>Session ID: {tokenInfo.sessionId || 'None'}</div>
                <div>User Email: {tokenInfo.userEmail || 'None'}</div>
                <div>Access Token: {getStatus(tokenInfo.accessToken === 'Present')}</div>
                <div>Refresh Token: {getStatus(tokenInfo.refreshToken === 'Present')}</div>
                <div>Expires At: {tokenInfo.expiresAt ? new Date(tokenInfo.expiresAt * 1000).toLocaleString() : 'None'}</div>
                <div>Auth Keys in Storage: {tokenInfo.authKeys.length}</div>
              </div>
              
              {tokenInfo.authKeys.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground">Storage Keys: {tokenInfo.authKeys.join(', ')}</p>
                </div>
              )}
              
              {(tokenInfo.sessionError || tokenInfo.userError) && (
                <div className="mt-2 text-red-600 text-sm">
                  {tokenInfo.sessionError && <p>Session Error: {tokenInfo.sessionError}</p>}
                  {tokenInfo.userError && <p>User Error: {tokenInfo.userError}</p>}
                </div>
              )}
            </div>
          )}

          {diagnostics && (
            <div>
              <h3 className="font-semibold mb-3">Database Authentication Context</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>auth.uid(): {getStatus(!!diagnostics.auth_uid)} {diagnostics.auth_uid || 'NULL'}</div>
                <div>JWT Sub Claim: {getStatus(!!diagnostics.jwt_sub_claim)} {diagnostics.jwt_sub_claim || 'NULL'}</div>
                <div>Current User: {diagnostics.current_user}</div>
                <div>Session User: {diagnostics.session_user}</div>
                <div>Timestamp: {new Date(diagnostics.timestamp).toLocaleString()}</div>
              </div>
              
              {diagnostics.jwt_claims && (
                <div className="mt-4">
                  <h4 className="font-medium mb-2">JWT Claims:</h4>
                  <pre className="bg-muted p-2 rounded text-xs overflow-auto">
                    {JSON.stringify(diagnostics.jwt_claims, null, 2)}
                  </pre>
                </div>
              )}
              
              <div className="mt-4 p-3 border rounded">
                <h4 className="font-medium mb-2">Diagnosis:</h4>
                {diagnostics.auth_uid ? (
                  <p className="text-green-600">✅ JWT tokens are reaching the database correctly</p>
                ) : (
                  <p className="text-red-600">❌ JWT tokens are NOT reaching the database. This is the root cause of RLS policy failures.</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};